import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import type {
  SealCardsDialogQuery,
  SealCardsDialogQueryVariables,
} from './__generated__/index.withFetch.graphql.ts';
import { SealCardsDialog } from './index.tsx';

const query = gql`
  query SealCardsDialogQuery(
    $slugs: [String!]!
    $sport: Sport!
    $rarity: Rarity!
  ) {
    config {
      id
      sealingConfig(sport: $sport) {
        ...SealCardsDialog_sealingConfig
      }
    }
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        rarityVaultBoost(rarity: $rarity, sport: $sport) {
          ...SealCardsDialog_rewardBoost
        }
      }
    }
    anyCards(slugs: $slugs) {
      slug
      ...SealCardsDialog_anyCard
    }
  }
  ${SealCardsDialog.fragments.anyCard}
  ${SealCardsDialog.fragments.sealingConfig}
  ${SealCardsDialog.fragments.rewardBoost}
` as TypedDocumentNode<SealCardsDialogQuery, SealCardsDialogQueryVariables>;

type Props = {
  open?: boolean;
  rarity: Rarity;
  selectedCardsSlugs?: string[];
  sealing?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const SealCardsDialogWithFetch = ({
  open = false,
  rarity,
  selectedCardsSlugs = [],
  sealing = false,
  onClose,
  onConfirm,
}: Props) => {
  const { sport } = useSportContext();
  const { data, loading } = useQuery(query, {
    variables: {
      slugs: selectedCardsSlugs,
      sport,
      rarity,
    },
    skip: !open || selectedCardsSlugs.length === 0,
  });

  const sealingConfig = data?.config?.sealingConfig;
  const cards = data?.anyCards || [];
  return (
    <SealCardsDialog
      open={Boolean(open && data)}
      rarity={rarity}
      loading={loading}
      sealing={sealing}
      cards={cards}
      rewardBoost={data?.currentUser?.anyUserSportProfile?.rarityVaultBoost}
      onClose={onClose}
      onConfirm={onConfirm}
      sealingConfig={sealingConfig}
    />
  );
};
